// src/layouts/SidebarLayout/components/SidebarMenu.tsx
import React, { useState } from 'react';
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  Typography,
  Chip,
  Tooltip,
  alpha,
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
  FiberManualRecord,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { SidebarLink } from '../../types';
import { useSidebar } from 'src/contexts/SidebarContext';


interface SidebarMenuProps {
  links: SidebarLink[];
  collapsed?: boolean;
  level?: number;
}

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => 
    !['isActive', 'isChild', 'collapsed'].includes(prop as string),
})<{
  isActive?: boolean;
  isChild?: boolean;
  collapsed?: boolean;
}>(({ theme, isActive, isChild, collapsed }) => ({
  borderRadius: theme.spacing(1),
  margin: theme.spacing(0.25, 0), // Reduced from 0.5
  padding: collapsed ? theme.spacing(0.75, 1.5) : theme.spacing(0.75, 1.5), // Reduced padding
  minHeight: collapsed ? 40 : 36, // Reduced height
  position: 'relative',
  transition: 'all 0.2s ease-in-out',
  
  ...(isChild && {
    marginLeft: theme.spacing(1.5), // Reduced from 2
    paddingLeft: theme.spacing(2.5), // Reduced from 3
    minHeight: 32, // Even smaller for children
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 16, // Adjusted for smaller padding
      top: 0,
      bottom: 0,
      width: 2,
      backgroundColor: alpha(theme.palette.primary.main, 0.2),
      borderRadius: 1,
    },
  }),

  ...(isActive && {
    backgroundColor: `${theme.palette.primary.main} !important`,
    color: theme.palette.primary.contrastText,
    boxShadow: theme.shadows[2], // Reduced shadow
    transform: 'translateX(2px)', // Reduced transform
    
    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.contrastText,
    },
    '& .MuiListItemText-primary': {
      color: theme.palette.primary.contrastText,
      fontWeight: 600,
    },
    '& .sidebar-badge': {
      backgroundColor: theme.palette.primary.contrastText,
      color: theme.palette.primary.main,
    },
  }),

  ...(!isActive && {
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
      transform: 'translateX(1px)', // Reduced transform
      
      '& .MuiListItemIcon-root': {
        color: theme.palette.primary.main,
      },
      '& .MuiListItemText-primary': {
        color: theme.palette.text.primary,
      },
    },
  }),

  '& .MuiListItemIcon-root': {
    minWidth: collapsed ? 0 : 32, // Reduced from 40
    color: isActive ? theme.palette.primary.contrastText : theme.palette.text.secondary,
    transition: 'color 0.2s ease-in-out',
    justifyContent: 'center',
    marginRight: collapsed ? 0 : theme.spacing(1), // Add some spacing when not collapsed
  },

  '& .MuiListItemText-primary': {
    color: isActive ? theme.palette.primary.contrastText : theme.palette.text.primary,
    fontWeight: isActive ? 600 : 500,
    fontSize: '0.85rem', // Slightly smaller font
    transition: 'color 0.2s ease-in-out',
  },
}));

const ExpandButton = styled(Box)<{ isActive?: boolean }>(({ theme, isActive }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 20, // Reduced from 24
  height: 20,
  borderRadius: theme.spacing(0.5),
  backgroundColor: isActive 
    ? alpha(theme.palette.primary.contrastText, 0.2)
    : alpha(theme.palette.action.active, 0.1),
  transition: 'all 0.2s ease-in-out',
  flexShrink: 0, // Prevent shrinking
  
  '& .MuiSvgIcon-root': {
    fontSize: 14, // Reduced from 16
    color: isActive 
      ? theme.palette.primary.contrastText 
      : theme.palette.text.secondary,
  },
}));

const CategoryHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 1.5, 0.5, 1.5), // Reduced padding
  marginTop: theme.spacing(1), // Reduced from 2
  '&:first-of-type': {
    marginTop: theme.spacing(0.25),
  },
}));

const SidebarMenuItem: React.FC<{
  link: SidebarLink;
  collapsed: boolean;
  level: number;
}> = ({ link, collapsed, level }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toggleCollapse, setCollapsed } = useSidebar();
  const [isOpen, setIsOpen] = useState(false);
  
  const isActive = location.pathname === link.route;
  const hasChildren = link.children && link.children.length > 0;
  const isChild = level > 0;
  
  // Check if any child is active
  const hasActiveChild = hasChildren && 
    link.children!.some(child => location.pathname === child.route);

  const handleClick = (event: React.MouseEvent) => {
    event.preventDefault();
    
    if (hasChildren) {
      if (collapsed) {
        // If collapsed and has children, expand sidebar and open this group
        setCollapsed(false);
        setTimeout(() => setIsOpen(true), 150); // Small delay for smooth animation
      } else {
        setIsOpen(!isOpen);
      }
    } else if (link.route) {
      navigate(link.route);
    }
  };

  const menuItem = (
    <StyledListItemButton
      onClick={handleClick}
      isActive={isActive}
      isChild={isChild}
      collapsed={collapsed}
      sx={{ cursor: 'pointer' }}
    >
      <ListItemIcon>
        {isChild ? (
          <FiberManualRecord sx={{ fontSize: 6 }} />
        ) : (
          link.icon
        )}
      </ListItemIcon>
      
      {!collapsed && (
        <>
          <ListItemText 
            primary={link.label}
            sx={{ flex: 1, minWidth: 0 }} // Allow text to shrink
          />
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            {link.badge && (
              <Chip
                label={link.badge}
                size="small"
                className="sidebar-badge"
                sx={{
                  height: 18, // Reduced from 20
                  fontSize: '0.65rem', // Smaller font
                  fontWeight: 600,
                  '& .MuiChip-label': {
                    px: 0.75, // Reduced padding
                  }
                }}
              />
            )}
            
            {hasChildren && (
              <ExpandButton isActive={isActive || hasActiveChild}>
                {isOpen ? <ExpandLess /> : <ExpandMore />}
              </ExpandButton>
            )}
          </Box>
        </>
      )}
    </StyledListItemButton>
  );

  // For collapsed state, show tooltip with hierarchy info
  if (collapsed && !isChild) {
    const tooltipContent = hasChildren 
      ? `${link.label} (${link.children!.length} items)`
      : link.label;
    
    return (
      <Tooltip
        title={tooltipContent}
        placement="right"
        arrow
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              fontSize: '0.75rem',
              maxWidth: 200,
            },
          },
        }}
      >
        <Box>{menuItem}</Box>
      </Tooltip>
    );
  }

  return (
    <>
      {menuItem}
      {hasChildren && !collapsed && (
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <List disablePadding sx={{ pl: 0.5 }}> {/* Reduced padding */}
            {link.children!.map((child) => (
              <SidebarMenuItem
                key={child.id}
                link={child}
                collapsed={false}
                level={level + 1}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

const SidebarMenu: React.FC<SidebarMenuProps> = ({
  links,
  collapsed = false,
  level = 0,
}) => {
  const visibleLinks = links.filter(link => link.visible !== false);

  if (visibleLinks.length === 0) {
    return null;
  }

  if (!collapsed && level === 0) {
    const groupedLinks = visibleLinks.reduce((acc, link) => {
      const category = link.category || 'uncategorized';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(link);
      return acc;
    }, {} as Record<string, typeof visibleLinks>);

    return (
      <Box sx={{ width: '100%' }}>
        {Object.entries(groupedLinks).map(([category, categoryLinks]) => (
          <Box key={category}>
            {category !== 'dashboard' && category !== 'uncategorized' && (
              <CategoryHeader>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.disabled',
                    fontWeight: 600,
                    fontSize: '0.65rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  {category.replace(/([A-Z])/g, ' $1').trim()}
                </Typography>
              </CategoryHeader>
            )}
            <List disablePadding sx={{ px: 0.5 }}> 
              {categoryLinks.map((link) => (
                <SidebarMenuItem
                  key={link.id}
                  link={link}
                  collapsed={collapsed}
                  level={level}
                />
              ))}
            </List>
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <List disablePadding sx={{ width: '100%', px: collapsed ? 0.5 : 0 }}>
      {visibleLinks.map((link) => (
        <SidebarMenuItem
          key={link.id}
          link={link}
          collapsed={collapsed}
          level={level}
        />
      ))}
    </List>
  );
};

export default SidebarMenu;